import React, { useState, useEffect } from 'react';
import i18next from 'core/i18n';
import {
  DialogContent,
  Select,
  SelectChangeEvent,
  Textarea,
  Checkbox,
  Body,
  useFlashMessage
} from '@indigo-ag/component-module';
import { ActivityType, Credit, Grain } from '@indigo-ag/schema';

import { ClientDataSectionProps } from '../../ClientDataSection';
import {
  EnhancedDialog,
  utils,
  activityTypeOptions,
  grainTranslations,
  createStylesHook,
  ModalActionButtons
} from 'shared';

const useStyles = createStylesHook({
  mt30: {
    marginTop: '30px !important'
  },
  mr32: {
    marginRight: 32
  },
  selectGrainsTitle: {
    margin: '30px 0 10px 0 !important',
    textTransform: 'capitalize'
  }
});

export interface ClientDataModalProps extends ClientDataSectionProps {
  closeModal: () => void;
  open: boolean;
  // TODO create type for function
  updateCredit: (newCreditInfo: Partial<Credit>) => Promise<unknown>;
}

export const ClientDataModal: React.FunctionComponent<ClientDataModalProps> = ({
  closeModal,
  open,
  clientDataInfo,
  updateCredit
}) => {
  /**
   * State
   */
  const [activityTypeSelected, setActivityTypeSelected] = useState<ActivityType | null>(
    clientDataInfo?.customerActivityType || null
  );
  const [grainsSelection, setGrainsSelection] = useState<Grain[]>(clientDataInfo?.customerProducts || []);
  const [description, setDescription] = useState(clientDataInfo?.customerDescription || '');

  /**
   * Styles
   */
  const classes = useStyles();

  useEffect(() => {
    setActivityTypeSelected(clientDataInfo?.customerActivityType || null);
    setGrainsSelection(clientDataInfo?.customerProducts || []);
    setDescription(clientDataInfo?.customerDescription || '');
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateActivityTypeSelected = (event: SelectChangeEvent<ActivityType>) => {
    setActivityTypeSelected(event.target.value);
  };

  const addRemoveGrain = (grainClicked: Grain) => () => {
    if (!grainsSelection.includes(grainClicked)) {
      setGrainsSelection([...grainsSelection, grainClicked]);
    } else {
      setGrainsSelection([...grainsSelection.filter(g => g !== grainClicked)]);
    }
  };

  const updateDescription = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(event.target.value);
  };

  const { addFlashMessage } = useFlashMessage();

  const updateInfo = () => {
    return updateCredit({
      customerActivityType: activityTypeSelected!,
      customerProducts: activityTypeSelected === ActivityType.GRAIN_PRODUCTION ? grainsSelection : [],
      customerDescription: description
    }).then(() => {
      addFlashMessage({
        content: `${utils.text.toTitleCase(i18next.t('clientData'))} ${i18next.t('hasBeenUpdatedSuccessfully')}`,
        title: '',
        variant: 'success'
      });
    });
  };

  return (
    <EnhancedDialog open={open} onClose={closeModal} title={i18next.t('clientData')}>
      <DialogContent>
        <Select
          label={utils.text.toTitleCase(i18next.t('activityType'))}
          name="activity-type"
          value={activityTypeSelected}
          options={activityTypeOptions}
          onChange={updateActivityTypeSelected}
          placeholder={i18next.t('pleaseSelect')}
        />

        {activityTypeSelected && activityTypeSelected === ActivityType.GRAIN_PRODUCTION ? (
          <>
            <Body className={classes.selectGrainsTitle}>{i18next.t('selectAllThatApply')}</Body>
            <div style={{ display: 'flex' }}>
              {Object.keys(Grain).map(grain => {
                return (
                  <Checkbox
                    key={grain}
                    checked={grainsSelection.includes(grain as Grain)}
                    onChange={addRemoveGrain(grain as Grain)}
                    label={utils.text.toTitleCase(grainTranslations[grain])}
                    className={classes.mr32}
                  />
                );
              })}
            </div>
          </>
        ) : null}
        {activityTypeSelected ? (
          <Textarea
            value={description}
            onChange={updateDescription}
            label={utils.text.toTitleCase(`${i18next.t('description')} (${i18next.t('optional')})`)}
            resizable
            rows={4}
            className={classes.mt30}
          />
        ) : null}
      </DialogContent>
      <ModalActionButtons
        changeButtonName={i18next.t('saveChanges')}
        closeModal={closeModal}
        disabled={
          !activityTypeSelected || (activityTypeSelected === ActivityType.GRAIN_PRODUCTION && !grainsSelection.length)
        }
        save={updateInfo}
      />
    </EnhancedDialog>
  );
};
